/**
 * Taxonomy_Service.ts
 * Categories, Collections, Tags + item assignment
 * (sk-catalog-api /api/v1/categories | /collections | /tags | /items/:id/{categories,tags})
 *
 * Request bodies match the backend DTO exactly (camelCase).
 */

import axiosInstance from '../../api/axiosInstance';
import {
  Catalog_Categories_Url,
  Catalog_Category_Url,
  Catalog_Item_Categories_Url,
  Catalog_Collections_Url,
  Catalog_Collection_Url,
  Catalog_Collection_Items_Url,
  Catalog_Collection_Item_Url,
  Catalog_Collection_Items_Order_Url,
  Catalog_Tags_Url,
  Catalog_Tag_Url,
  Catalog_Item_Tags_Url,
} from '../../api/endpoints';
import {
  Category,
  Collection,
  Tag,
  CreateCategoryPayload,
  CreateCollectionPayload,
  CreateTagPayload,
  SetItemCategoriesPayload,
  AddCollectionItemsPayload,
  SetItemTagsPayload,
  TaxonomyResponse,
} from '../../schemas/catalog/taxonomy';

/* ═══════════════════════════════════════
   CATEGORIES
═══════════════════════════════════════ */

export const getCategories = async (): Promise<TaxonomyResponse<Category>> => {
  const { data } = await axiosInstance.get<TaxonomyResponse<Category>>(Catalog_Categories_Url);
  return data;
};

export const getCategory = async (id: string): Promise<Category> => {
  const { data } = await axiosInstance.get<Category>(Catalog_Category_Url(id));
  return data;
};

export const createCategory = async (payload: CreateCategoryPayload): Promise<Category> => {
  const { data } = await axiosInstance.post<Category>(Catalog_Categories_Url, payload);
  return data;
};

export const updateCategory = async (id: string, payload: Partial<CreateCategoryPayload>): Promise<Category> => {
  const { data } = await axiosInstance.patch<Category>(Catalog_Category_Url(id), payload);
  return data;
};

export const deleteCategory = async (id: string): Promise<void> => {
  await axiosInstance.delete(Catalog_Category_Url(id));
};

/** Replace an item's category assignments — PUT /api/v1/items/:id/categories */
export const setItemCategories = async (itemId: string, payload: SetItemCategoriesPayload): Promise<any> => {
  const { data } = await axiosInstance.put(Catalog_Item_Categories_Url(itemId), payload);
  return data;
};

/* ═══════════════════════════════════════
   COLLECTIONS
═══════════════════════════════════════ */

export const getCollections = async (): Promise<TaxonomyResponse<Collection>> => {
  const { data } = await axiosInstance.get<TaxonomyResponse<Collection>>(Catalog_Collections_Url);
  return data;
};

export const getCollection = async (id: string): Promise<Collection> => {
  const { data } = await axiosInstance.get<Collection>(Catalog_Collection_Url(id));
  return data;
};

export const createCollection = async (payload: CreateCollectionPayload): Promise<Collection> => {
  const { data } = await axiosInstance.post<Collection>(Catalog_Collections_Url, payload);
  return data;
};

export const updateCollection = async (id: string, payload: Partial<CreateCollectionPayload>): Promise<Collection> => {
  const { data } = await axiosInstance.patch<Collection>(Catalog_Collection_Url(id), payload);
  return data;
};

export const deleteCollection = async (id: string): Promise<void> => {
  await axiosInstance.delete(Catalog_Collection_Url(id));
};

/** Add items to a collection — POST /api/v1/collections/:id/items */
export const addCollectionItems = async (id: string, payload: AddCollectionItemsPayload): Promise<any> => {
  const { data } = await axiosInstance.post(Catalog_Collection_Items_Url(id), payload);
  return data;
};

/** Update an item's position in a collection — PATCH .../items/:itemId */
export const updateCollectionItem = async (id: string, itemId: string, payload: { sortOrder?: number }): Promise<any> => {
  const { data } = await axiosInstance.patch(Catalog_Collection_Item_Url(id, itemId), payload);
  return data;
};

/** Remove an item from a collection — DELETE .../items/:itemId */
export const removeCollectionItem = async (id: string, itemId: string): Promise<void> => {
  await axiosInstance.delete(Catalog_Collection_Item_Url(id, itemId));
};

/** Batch reorder collection items — PUT .../items/order */
export const reorderCollectionItems = async (
  id: string,
  order: Array<{ catalogItemId: string; sortOrder: number }>,
): Promise<void> => {
  await axiosInstance.put(Catalog_Collection_Items_Order_Url(id), { order });
};

/* ═══════════════════════════════════════
   TAGS
═══════════════════════════════════════ */

export const getTags = async (): Promise<TaxonomyResponse<Tag>> => {
  const { data } = await axiosInstance.get<TaxonomyResponse<Tag>>(Catalog_Tags_Url);
  return data;
};

export const createTag = async (payload: CreateTagPayload): Promise<Tag> => {
  const { data } = await axiosInstance.post<Tag>(Catalog_Tags_Url, payload);
  return data;
};

export const updateTag = async (id: string, payload: Partial<CreateTagPayload>): Promise<Tag> => {
  const { data } = await axiosInstance.patch<Tag>(Catalog_Tag_Url(id), payload);
  return data;
};

export const deleteTag = async (id: string): Promise<void> => {
  await axiosInstance.delete(Catalog_Tag_Url(id));
};

/** Replace an item's tag assignments — PUT /api/v1/items/:id/tags */
export const setItemTags = async (itemId: string, payload: SetItemTagsPayload): Promise<any> => {
  const { data } = await axiosInstance.put(Catalog_Item_Tags_Url(itemId), payload);
  return data;
};
